import type { ProviderUsage, TaskUsage } from '../../api/types'
import { formatCredits, formatDuration, formatMicroDollars } from '../../utils/format'

/** How each provider writes its own name; one we have not met yet reads as the id we log it under. */
export const PROVIDER_NAMES: Record<string, string> = {
  cloudflare: 'Cloudflare',
  fal: 'fal',
  indiefuture: 'our own workers',
  openai: 'OpenAI',
  typesafe: 'TypeSafe',
}

/** What a task's units are: audio or clip time, images, or nothing (tokens have their own columns). */
function units(task: TaskUsage): string {
  if (task.units === 0) return '—'
  if (task.task === 'transcribe' || task.task === 'clip_render') return formatDuration(task.units)
  if (task.task === 'thumbnail') return `${task.units.toLocaleString('en-US')} images`
  return '—'
}

const tokens = (n: number) => (n === 0 ? '—' : n >= 10_000 ? `${Math.round(n / 1000).toLocaleString('en-US')}k` : n.toLocaleString('en-US'))

/** Calls by task and model: volume, what providers billed, what was charged, and the margin between. */
export function TaskUsageTable({ tasks }: { tasks: TaskUsage[] }) {
  if (tasks.length === 0) return <p className="muted">No calls in this period.</p>
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Task</th>
            <th>Model</th>
            <th className="num">Calls</th>
            <th className="num">Audio / images</th>
            <th className="num">Tokens in / out</th>
            <th className="num">Provider cost</th>
            <th className="num">Credits</th>
            <th className="num">Margin</th>
            <th className="num">Avg time</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => {
            const margin = task.credits_charged / 1000 - task.provider_cost_micros
            return (
              <tr key={`${task.task}/${task.provider}/${task.model}`}>
                <td>
                  {task.task}
                  {task.failures > 0 && <span className="small error-text"> · {task.failures} failed</span>}
                </td>
                <td className="muted small truncate" title={`${task.provider} ${task.model}`}>
                  {task.provider} · {task.model || '—'}
                </td>
                <td className="num">{task.calls.toLocaleString('en-US')}</td>
                <td className="num">{units(task)}</td>
                <td className="num">
                  {tokens(task.input_tokens)} / {tokens(task.output_tokens)}
                </td>
                <td className="num">
                  {formatMicroDollars(task.provider_cost_micros)}
                  {task.unpriced_calls > 0 && <span className="small warn-text" title="Calls to a model without a provider price"> +{task.unpriced_calls} unpriced</span>}
                </td>
                <td className="num">{formatCredits(task.credits_charged, 'exact')}</td>
                <td className={`num ${margin < 0 ? 'error-text' : ''}`}>{formatMicroDollars(margin)}</td>
                <td className="num muted">{task.avg_latency_ms == null ? '—' : formatMs(task.avg_latency_ms)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** Calls by provider, whatever the task: what each one bills us against what we charge for it. */
export function ProviderUsageTable({ providers }: { providers: ProviderUsage[] }) {
  if (providers.length === 0) return <p className="muted">No calls in this period.</p>
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Provider</th>
            <th className="num">Calls</th>
            <th className="num">Tokens in / out</th>
            <th className="num">Provider cost</th>
            <th className="num">Credits</th>
            <th className="num">Margin</th>
          </tr>
        </thead>
        <tbody>
          {providers.map((provider) => {
            const margin = provider.credits_charged / 1000 - provider.provider_cost_micros
            return (
              <tr key={provider.provider}>
                <td>
                  {PROVIDER_NAMES[provider.provider] ?? provider.provider}
                  {provider.failures > 0 && <span className="small error-text"> · {provider.failures} failed</span>}
                </td>
                <td className="num">{provider.calls.toLocaleString('en-US')}</td>
                <td className="num">
                  {tokens(provider.input_tokens)} / {tokens(provider.output_tokens)}
                </td>
                <td className="num">
                  {formatMicroDollars(provider.provider_cost_micros)}
                  {provider.unpriced_calls > 0 && <span className="small warn-text" title="Calls to a model without a provider price"> +{provider.unpriced_calls} unpriced</span>}
                </td>
                <td className="num">{formatCredits(provider.credits_charged, 'exact')}</td>
                <td className={`num ${margin < 0 ? 'error-text' : ''}`} title={margin < 0 ? 'We absorb this: the work is free to the workspace' : undefined}>
                  {formatMicroDollars(margin)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** A job or call length: milliseconds under a second, seconds under a minute. */
export function formatMs(ms: number): string {
  if (ms < 1000) return `${ms} ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`
  return formatDuration(ms)
}
